/**
 * Canonical form-builder schema.
 *
 * This model is the single source of truth for a form's structure. The backend
 * Forms service is expected to round-trip a `FormDefinition` losslessly (see
 * docs and the project memory note "form-schema-canonical-source").
 */

import {ReviewType} from './submission.model';

export {ReviewType};

export type FormStatus = 'draft' | 'published' | 'archived';

export type FormDefinition = {
  id: string;
  key: string;
  title: string;
  description?: string;
  version: number;
  status: FormStatus;
  pages: FormPage[];
  /**
   * Cross-field validations evaluated against the whole form value. When a
   * rule group evaluates to `true` the form is considered invalid and the
   * associated {@link CrossFieldValidation.message} is shown.
   */
  validations?: CrossFieldValidation[];
  /**
   * Which human reviews a submission of this form requires (e.g. payroll). A submission is "open" for a review
   * type until a reviewer marks it handled; un-reviewed submissions surface in the payroll Excel export. Empty /
   * absent ⇒ the form needs no review. Mirrors the backend ReviewType enum (serialised as numeric values), read
   * server-side from the definition on publish (FormDocument.RequiredReviews).
   */
  requiredReviews?: ReviewType[];
  /**
   * How long a completed submission of this form stays valid, in days. Once `submittedAt + validityPeriodDays`
   * has passed the answer counts as **expired** and is treated exactly like a review flag: the employee keeps
   * full access for the fixed 14-day grace and is restricted afterwards, until a fresh submission replaces it.
   * `null` / absent ⇒ **unbegrenzt gültig** (never expires), which is the default. Read server-side from the
   * definition on publish (FormDocument.ValidityPeriodDays), so expiry is derived from one queryable column.
   */
  validityPeriodDays?: number | null;
};

export type FormPage = {
  id: string;
  title: string;
  blocks: FormBlock[];
};

export type FormBlock =
  | FieldBlock
  | GroupBlock
  | ListBlock
  | StaticTextBlock;

export type BaseBlock = {
  id: string;
  type: string;
  label?: string;
  /** Block is rendered only when this rule group evaluates to true. */
  visibleWhen?: RuleGroup;
  /** Control is enabled only when this rule group evaluates to true. */
  enabledWhen?: RuleGroup;
  /** Control becomes required when this rule group evaluates to true. */
  requiredWhen?: RuleGroup;
};

/**
 * Concrete input field types supported by the builder. `select`, `multiselect`,
 * `radio` and `checkbox-group` are option-based.
 */
export type FieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'radio'
  | 'checkbox-group'
  | 'date'
  | 'time'
  | 'boolean'
  | 'file'
  | 'signature'
  // Inherently-sensitive semantic types — always encrypted at rest with a default mask (see SENSITIVE_FIELD_TYPES).
  | 'iban'
  | 'bank'
  | 'idnumber'
  | 'taxid'
  | 'socialsecurity';

export type FieldOption = { label: string; value: any };

/**
 * A dynamic default value, resolved at form-build time instead of being stored as a literal — so a form
 * published in January still opens on *today's* date. Resolution happens client-side, in the browser's
 * time zone (the employee's own local day is what they expect to see).
 */
export type DefaultToken = 'today' | 'now';

/**
 * Every concrete field type, as a compile-time-exhaustive map. Adding a member to {@link FieldType} without
 * adding it here is a build error — which is the point: the default-value editor is driven off this list, so
 * a new type cannot silently slip past it.
 */
const FIELD_TYPE_MAP: Record<FieldType, true> = {
  text: true, textarea: true, email: true, phone: true, number: true,
  select: true, multiselect: true, radio: true, 'checkbox-group': true,
  date: true, time: true, boolean: true, file: true, signature: true,
  iban: true, bank: true, idnumber: true, taxid: true, socialsecurity: true
};

/** Every concrete field type (i.e. everything except the `group` / `list` / `static-text` containers). */
export const ALL_FIELD_TYPES: FieldType[] = Object.keys(FIELD_TYPE_MAP) as FieldType[];

/** Every block that is not a value-bearing field: the containers plus static text. */
export type NonFieldBlock = Exclude<FormBlock, FieldBlock>;

/**
 * The non-field block types, as a compile-time-exhaustive map — the same trick as {@link FIELD_TYPE_MAP}, and
 * for the same reason. Adding a member to {@link FormBlock} without adding it here is a build error.
 *
 * This exists so tests can enumerate **every** block type from the schema instead of a hand-written list. #490
 * was a whole block type (`static-text`) silently missing from the visibility walk, and no test noticed because
 * every test named the types it cared about.
 */
const NON_FIELD_BLOCK_TYPE_MAP: Record<NonFieldBlock['type'], true> = {
  group: true, list: true, 'static-text': true
};

/** Every non-field block type. */
export const NON_FIELD_BLOCK_TYPES: NonFieldBlock['type'][] =
  Object.keys(NON_FIELD_BLOCK_TYPE_MAP) as NonFieldBlock['type'][];

/**
 * The three conditional rules a block can declare (see {@link BaseBlock}).
 *
 * The schema puts all three on every block because they share one shape — but what the **runtime** can honour
 * depends on the block type, and offering an author a rule that silently does nothing is a defect in its own
 * right (#491).
 */
export type ConditionKey = 'visibleWhen' | 'enabledWhen' | 'requiredWhen';

/** All three, in the order the builder presents them. */
export const ALL_CONDITION_KEYS: ConditionKey[] = ['visibleWhen', 'enabledWhen', 'requiredWhen'];

/**
 * What each **non-field** block type can honour. Exhaustive over `NonFieldBlock['type']`, so a block type added
 * to {@link FormBlock} cannot be introduced without declaring its support here.
 *
 * - `group` — `visibleWhen` hides the subtree, `enabledWhen` disables the backing `FormGroup` and with it every
 *   descendant (#492). **No `requiredWhen`**, and this one is measurable rather than a matter of taste:
 *   `Validators.required` on a `FormGroup` can never fail. `isEmptyInputValue` delegates to `lengthOrSize`,
 *   which returns `null` for a plain object (it only understands arrays, strings and Sets), and `null === 0` is
 *   false — so the validator always passes. Offering it would be offering a guaranteed no-op.
 * - `list` — the same two, **plus `requiredWhen`**: on a `FormArray` the identical validator *does* fail,
 *   because `lengthOrSize` returns `value.length`, so an empty list is invalid. That is the only "at least one
 *   entry" mechanism there is — `minItems` seeds rows and gates the remove button, it never validates, and it
 *   defaults to `0` (#493).
 * - `static-text` — `visibleWhen` only. It has no form control at all, so "enabled" and "required" are not
 *   merely unused, they are meaningless.
 */
const NON_FIELD_CONDITION_SUPPORT: Record<NonFieldBlock['type'], readonly ConditionKey[]> = {
  group: ['visibleWhen', 'enabledWhen'],
  list: ['visibleWhen', 'enabledWhen', 'requiredWhen'],
  'static-text': ['visibleWhen']
};

/**
 * The conditions a block type can actually honour.
 *
 * Field types are uniform by construction — every field has a control, so it can be hidden, disabled and made
 * required — which is why they are not enumerated one by one: 19 identical rows would be noise that invites a
 * copy-paste mistake, and a *new* field type inheriting all three is correct rather than a silent no-op. The
 * distinction that carries meaning is field vs. non-field, and that is the one the map above forces.
 */
export function conditionsFor(type: FieldType | NonFieldBlock['type'] | string): readonly ConditionKey[] {
  return NON_FIELD_CONDITION_SUPPORT[type as NonFieldBlock['type']] ?? ALL_CONDITION_KEYS;
}

/** Whether a block of this type honours this condition at runtime. */
export function supportsCondition(
  type: FieldType | NonFieldBlock['type'] | string, condition: ConditionKey): boolean {
  return conditionsFor(type).includes(condition);
}

/** Every block type the schema supports, fields and non-fields alike. */
export const ALL_BLOCK_TYPES: (FieldType | NonFieldBlock['type'])[] =
  [...ALL_FIELD_TYPES, ...NON_FIELD_BLOCK_TYPES];

/**
 * Field types that cannot carry a default value: a file upload and a drawn signature have no meaningful
 * literal an author could pre-set.
 */
export const DEFAULT_VALUE_UNSUPPORTED_TYPES: FieldType[] = ['file', 'signature'];

/** Whether the builder offers a default-value editor for this block type. */
export function supportsDefaultValue(type: FieldType | string | undefined): boolean {
  return !!type
    && ALL_FIELD_TYPES.includes(type as FieldType)
    && !DEFAULT_VALUE_UNSUPPORTED_TYPES.includes(type as FieldType);
}

/** The dynamic tokens offered per field type. A type absent here supports literal defaults only. */
export const DEFAULT_TOKENS_FOR_TYPE: { [type: string]: DefaultToken[] } = {
  date: ['today'],
  time: ['now']
};

/** The dynamic tokens available for a field type (empty when it has none). */
export function defaultTokensFor(type: FieldType | string | undefined): DefaultToken[] {
  return type ? DEFAULT_TOKENS_FOR_TYPE[type] ?? [] : [];
}

/**
 * How a sensitive value is masked when shown to a non-privileged viewer. Mirrors the backend MaskStyle enum
 * (CheqMe.Server.Services.Forms.Domain.Security.MaskStyle); values are parsed case-insensitively server-side.
 */
export type MaskStyle = 'none' | 'full' | 'last4' | 'first4' | 'email' | 'iban' | 'custom';

export type FieldMask = {
  style: MaskStyle;
  /** Regex whose matches are masked, used when style === 'custom'. */
  pattern?: string;
  /** How many characters stay visible for last4 / first4. */
  visibleChars?: number;
};

export type FieldValidators = {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  minDate?: string;
  maxDate?: string;
  pattern?: string;
  email?: boolean;
  /** Custom, human-friendly override messages keyed by Angular error code. */
  messages?: { [errorKey: string]: string };
};

export type FieldBlock = BaseBlock & {
  type: FieldType;
  key: string;
  required?: boolean;
  placeholder?: string;
  /** Used by select / multiselect / radio / checkbox-group. */
  options?: FieldOption[];
  /** Allow selecting multiple values (multiselect / file). */
  multiple?: boolean;
  validators?: FieldValidators;
  /**
   * Encrypt this field's value at rest and mask it on read. Inherently-sensitive types
   * (see {@link SENSITIVE_FIELD_TYPES}) are always treated as sensitive regardless of this flag — the backend
   * decides sensitivity from the stored definition, so this is an additive author override only.
   */
  sensitive?: boolean;
  /** How the value is masked when shown unprivileged. Absent ⇒ the type's default (see DEFAULT_MASK_FOR_TYPE). */
  mask?: FieldMask;
  /**
   * Literal value prefilled into a **new** submission. Never applied when an existing submission is
   * restored — a stored value always wins, including a stored empty one. Ignored for the types in
   * {@link DEFAULT_VALUE_UNSUPPORTED_TYPES}, and for option-based fields when it matches none of the
   * field's current {@link FieldBlock.options} (so deleting an option cannot leave a phantom selection).
   */
  defaultValue?: any;
  /**
   * Dynamic default resolved at form-build time in the browser's time zone, e.g. `today` for a date
   * field. Takes precedence over {@link FieldBlock.defaultValue} when both are set. Only the tokens in
   * {@link DEFAULT_TOKENS_FOR_TYPE} are valid for a given type; anything else is ignored.
   */
  defaultToken?: DefaultToken;
};

export type GroupBlock = BaseBlock & {
  type: 'group';
  key: string;
  blocks: FormBlock[];
};

export type ListBlock = BaseBlock & {
  type: 'list';
  key: string;
  blocks: FormBlock[];
  minItems?: number;
  maxItems?: number;
};

/**
 * How a {@link StaticTextBlock.content} string is authored, and therefore how a renderer must turn it into
 * markup.
 *
 * `html` is the historical (and only) behaviour: the content is a raw HTML fragment pushed through
 * `[innerHTML]`. `markdown` was added by #501 so an author can write a readable explanation without hand-writing
 * tags — the runtime converts it, and the PDF's server-side counterpart converts the same source with Markdig, so
 * one authored string reads identically on screen and on paper.
 *
 * The value is **optional on the block** and absent means `html` — see {@link staticTextFormat}. That default is
 * load-bearing rather than cosmetic: every form published before #501 stores no `format` at all, and any other
 * default would silently re-interpret their HTML as Markdown (`<p>` lines collapsing, `_` and `*` inside real
 * prose turning into emphasis). Legacy forms must keep rendering byte-for-byte as they do today.
 */
export type StaticTextFormat = 'html' | 'markdown';

/**
 * Every static-text format, as a compile-time-exhaustive map — the same trick as {@link FIELD_TYPE_MAP} and
 * {@link NON_FIELD_BLOCK_TYPE_MAP}, and for the same reason.
 *
 * Adding a member to {@link StaticTextFormat} without adding it here is a build error, which is the point: the
 * block editor's format picker and the runtime's render branch are both driven off this list, so a third format
 * cannot be introduced in the type alone and then silently fall through to the `html` default at runtime.
 */
const STATIC_TEXT_FORMAT_MAP: Record<StaticTextFormat, true> = {
  html: true, markdown: true
};

/** Every static-text format, in the order the builder offers them. */
export const ALL_STATIC_TEXT_FORMATS: StaticTextFormat[] =
  Object.keys(STATIC_TEXT_FORMAT_MAP) as StaticTextFormat[];

export type StaticTextBlock = BaseBlock & {
  type: 'static-text';
  key: string;
  content: string;
  /**
   * How {@link StaticTextBlock.content} is authored. **Absent means `html`** ({@link staticTextFormat}) so every
   * form published before #501 renders exactly as it did — see {@link StaticTextFormat}.
   */
  format?: StaticTextFormat;
  /**
   * The keys of the fields this text explains, so the explanation can follow its field into every read-back
   * view (the management detail page, the employee self-view and the PDF).
   *
   * **The link deliberately lives on the text, not on the field.** One paragraph routinely explains several
   * fields at once ("Die folgenden drei Angaben stammen aus Ihrem Sozialversicherungsausweis"), and the inverse
   * modelling — a `noteId` on each field — would force the author to repeat the same reference on every field
   * and would put an authoring concern into the value-bearing schema that submissions are validated against.
   * Fields stay untouched, which also means a form that never uses `explains` is bit-identical to before.
   *
   * A key that names no field in the definition is **ignored, not an error**: the field may simply have been
   * deleted since the text was written, and a dangling reference must never break rendering a submission.
   */
  explains?: string[];
};

/**
 * The effective format of a static-text block: its declared {@link StaticTextBlock.format}, or `html` when it
 * declares none.
 *
 * Also the guard against a bad value arriving over the wire. The definition is stored as a JSON document that
 * the backend round-trips verbatim, so nothing at the boundary rejects `"format": "asciidoc"` — resolving an
 * unknown value to `html` keeps the historical rendering rather than dropping the block or throwing.
 */
export function staticTextFormat(block: Pick<StaticTextBlock, 'format'> | null | undefined): StaticTextFormat {
  const format = block?.format;
  return format && ALL_STATIC_TEXT_FORMATS.includes(format) ? format : 'html';
}

export type RuleCondition = 'AND' | 'OR';

export type RuleGroup = {
  condition: RuleCondition;
  rules: (Rule | RuleGroup)[];
};

export type RuleOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'startsWith'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'isEmpty'
  | 'isNotEmpty';

export type Rule = {
  fieldKey: string;
  operator: RuleOperator;
  value?: any;
};

export type CrossFieldValidation = {
  id: string;
  message: string;
  /** When this evaluates to true, the form is invalid. */
  when: RuleGroup;
  /** Optional field key the error is attached to; otherwise attached to the form root. */
  targetKey?: string;
};

/** Type guard: does this block carry a data `key`? (everything except nothing today, but future-proofed) */
export function blockHasKey(block: FormBlock): block is FieldBlock | GroupBlock | ListBlock | StaticTextBlock {
  return 'key' in block;
}

/** Field types that present a list of options. */
export const OPTION_FIELD_TYPES: FieldType[] = ['select', 'multiselect', 'radio', 'checkbox-group'];

/** Field types whose value is an array. */
export const ARRAY_VALUE_FIELD_TYPES: FieldType[] = ['multiselect', 'checkbox-group'];

/**
 * Inherently-sensitive semantic types: always encrypted at rest, with the default mask below, and not
 * downgradable in the builder. Mirrors the backend FieldSensitivity catalog (the server is the source of truth;
 * this drives the UI so authors see the protection and cannot turn it off).
 */
export const SENSITIVE_FIELD_TYPES: FieldType[] = ['iban', 'bank', 'idnumber', 'taxid', 'socialsecurity'];

/** The default mask style applied to each inherently-sensitive type when no explicit mask is set. */
export const DEFAULT_MASK_FOR_TYPE: { [type: string]: MaskStyle } = {
  iban: 'iban',
  bank: 'iban',
  idnumber: 'last4',
  taxid: 'last4',
  socialsecurity: 'last4'
};

/** True for the inherently-sensitive semantic types. */
export function isSensitiveType(type: FieldType | string | undefined): boolean {
  return !!type && SENSITIVE_FIELD_TYPES.includes(type as FieldType);
}

/** Whether a field is sensitive: an inherently-sensitive type, or one the author flagged. */
export function isFieldSensitive(field: Pick<FieldBlock, 'type' | 'sensitive'>): boolean {
  return isSensitiveType(field.type) || !!field.sensitive;
}
